<script>
  import { onMount, onDestroy } from "svelte";
  import Toolbar from "./Toolbar.svelte";
  import Editor from "./Editor.svelte";
  import { array_move, getRandomInt, ignore } from "../Common/tools";
  import { UNSUBSCRIBE, FOOTER_TWO, FOOTER_THREE, FOOTER_ONE } from "./const";
  import EmailMarketingSettings, { verifyBrandDetails } from "../MarketingSettings/Tabs/Settings.svelte";
  import {External, M} from "../external";

  export let editorData,
    addUnsubscribeBlockOnEmpty = false,
    meta,
    options = {},
    onChange = ignore;

  editorData = editorData || {};
  editorData.content = editorData.content || [];
  editorData.style = editorData.style || {
    backgroundColor: "#EEF0F5",
    width: "680px",
    fontFamily: "Arial"
  };

  let activeData = editorData;
  let activeSection = "global";
  let isFixed = false;
  let toolbar, textarea, codeMirrorEditor;
  let pointer = 0;
  let history = [];
  let previousData;
  let previousStyle;
  let rawHtmlChanged = false;

  function onToolbarChange(data) {
    if (activeSection !== 0 && !activeSection) {
      return;
    }

    if (activeSection === "global") {
      editorData.style = Object.assign({}, editorData.style, data);
      onChange();
      return;
    }

    let style = editorData.content[activeSection].style;

    if (!style) {
      editorData.content[activeSection].style = {};
    }

    editorData.content[activeSection].style = Object.assign({}, style, data);
    onChange();
  }

  let onFocus = (section) => {
    if (activeSection !== "global" && previousData !== JSON.stringify(editorData.content[activeSection])) {
      saveHistory({
        index: activeSection,
        content: previousData,
        newContent: JSON.stringify(editorData.content[activeSection]),
        action: "change"
      });
    } else if (activeSection === "global" && previousStyle !== JSON.stringify(editorData.style)) {
      saveHistory({
        content: previousStyle,
        newContent: JSON.stringify(editorData.style),
        action: "global"
      });
    }

    activeSection = section;
    activeData = editorData.content[activeSection];
    previousData = JSON.stringify(editorData.content[activeSection]);
    previousStyle = JSON.stringify(editorData.style);

    toolbar.switchTabStying();
  };

  let onBlur = (data) => {
    if (data) {
      saveHistory(data);
    } else if (activeSection !== "global" && previousData !== JSON.stringify(editorData.content[activeSection])) {
      saveHistory({
        index: activeSection,
        content: previousData,
        newContent: JSON.stringify(editorData.content[activeSection]),
        action: "change"
      });
    }

    activeSection = "global";
    activeData = editorData;
    previousData = "";
  };

  export function get() {
    if (!options.html) return { content: editorData, ...meta };

    return { content: codeMirrorEditor.getValue() };
  }

  export function hasChange() {
    return rawHtmlChanged || history.length > 0;
  }

  let verifyEmail = () => {
    let unsubscribeBlocks = [];
    if (!options.html && editorData && editorData.content) {
      unsubscribeBlocks = editorData.content.filter((element) => {
        return element.type && (element.type === UNSUBSCRIBE || element.type === FOOTER_THREE || element.type === FOOTER_ONE || element.type === FOOTER_TWO);
      });
    }

    if (options.html) {
      // raw email
      let rawContent = editorData.content || "";
      if (rawContent.indexOf("{unsubscribe_link}") < 0) {
        External.Tools.showError(M.ADD_UNSUBSCRIBE_LINK);
        return false;
      }
      if (rawContent.indexOf("{company_name}") < 0 || rawContent.indexOf("{company_address}") < 0) {
        External.Tools.showError(M.ADD_BRAND_DETAILS);
        return false;
      }
    } else {
      if (!unsubscribeBlocks.length) {
        External.Tools.showError(M.ADD_UNSUBSCRIBE_BLOCK);
        return false;
      }
    }

    return true;
  };

  export async function verify() {
    let marketingSettings = await verifyBrandDetails();
    return verifyEmail();
  }

  let refreshRawContentDebounced = External.$.debounce(1000, (content) => {
    refreshRawContent();
  });

  let refreshRawContent = () => {
    let emailTarget = External.$("#rawEmailTarget");
    emailTarget.empty();

    let iframeId = "iframe" + getRandomInt();
    let iframe = document.createElement("iframe");
    iframe.src = "about:blank";
    iframe.style = "background: transparent; margin: 0px; padding: 0px; border: 0px;";
    iframe.width = iframe.height = "100%";
    iframe.id = iframeId;
    iframe.name = iframeId;
    emailTarget.append(iframe);

    let form = External.$('<form class="none" action="/me/email" method="post" target="' + iframeId + '"></form>');
    let input = External.$('<input type="hidden" name="content" />');
    input.val(JSON.stringify(editorData));
    form.append(input);
    External.$("body").append(form);
    form.submit();
    form.remove();
    form = null;
  };

  $: if (options.html) {
    refreshRawContentDebounced(editorData.content);
  }

  onMount(async function () {
    let brandSettings = await verifyBrandDetails();
    External.$(".working-area-wrapper-inner").css("overflow-y", "hidden");
    previousStyle = JSON.stringify(editorData.style);

    if (options.html) {
      codeMirrorEditor = External.Tools.highlight(textarea, {
        lineNumbers: true,
        readOnly: false,
        mode: "text/html",
        theme: "dark",
        onChange: function (editor) {
          editorData.content = editor.getValue();
          rawHtmlChanged = true;
        }
      });

      codeMirrorEditor.setSize("100%", "calc(100% - 120px)");
    }
  });

  onDestroy(function () {
    activeData = null;
    editorData = null;
    meta = null;
    External.$(".working-area-wrapper-inner").css("overflow-y", "");
  });

  function saveHistory(state) {
    if (pointer < history.length) {
      history.splice(pointer, history.length - pointer);
    }

    history.push(state);
    pointer = history.length;

    onChange();
  }

  function onEditorChange(index, ev) {
    if (ev == null || editorData == null) {
      return;
    }
    if (ev === "padding") {
      setTimeout(function () {
        if (!editorData) return;
        saveHistory({
          index: index,
          action: "change",
          content: previousData,
          newContent: JSON.stringify(editorData.content[index])
        });
      });
      return;
    }
    let content = JSON.stringify(editorData.content[index]);
    setTimeout(function () {
      if (!editorData) return;
      saveHistory({
        index: index,
        action: ev !== "add" ? "change" : "add",
        content: content,
        newContent: JSON.stringify(editorData.content[index])
      });
    });
  }

  function removeItem(array, n) {
    return array.filter((elem, i) => i !== n);
  }

  function undo() {
    if (pointer === 0) {
      return;
    }

    pointer -= 1;
    let undo = history[pointer];

    if (undo.action === "change") {
      editorData.content[undo.index] = JSON.parse(undo.content);
    } else if (undo.action === "up" || undo.action === "down") {
      editorData.content = array_move(editorData.content, undo.from, undo.to);
    } else if (undo.action === "remove") {
      const item = JSON.parse(undo.content);
      const itemId = editorData.content.push(item);
      editorData.content = array_move(editorData.content, itemId - 1, undo.index);
    } else if (undo.action === "clone" || undo.action === "add") {
      editorData.content = removeItem(editorData.content, undo.index);
    } else if (undo.action === "global") {
      editorData.style = JSON.parse(undo.content);
    }

    activeSection = "global";
    activeData = editorData;

    onChange();
  }

  function redo() {
    if (pointer === history.length) {
      return;
    }
    onChange();

    activeSection = "global";
    activeData = editorData;

    let redo = history[pointer];
    pointer += 1;

    if (redo.action === "change") {
      editorData.content[redo.index] = JSON.parse(redo.newContent);
    } else if (redo.action === "up" || redo.action === "down") {
      editorData.content = array_move(editorData.content, redo.to, redo.from);
    } else if (redo.action === "remove") {
      editorData.content = removeItem(editorData.content, redo.index);
    } else if (redo.action === "clone" || redo.action === "add") {
      const item = JSON.parse(redo.content);
      const itemId = editorData.content.push(item);
      editorData.content = array_move(editorData.content, itemId - 1, redo.index);
    } else if (redo.action === "global") {
      editorData.style = JSON.parse(redo.newContent);
    }
  }
</script>

{#if !options.html}
  <style>
    .floating-buttons {
      position: absolute;
      z-index: 1;
      right: 40px;
      top: 62px;
    }

    .floating-buttons .material-icons {
      font-size: 28px;
    }

    .floating-buttons .rounded-effect {
      cursor: pointer;
    }

    .floating-buttons .rounded-effect.disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  </style>
  <div class="email-builder-content flex flex-row flex-1" style="background-color: {editorData.style.backgroundColor}">
    <div class="floating-buttons">
      <a class="rounded-effect" class:disabled={pointer === 0} on:click={undo}>
        <span title="undo" class="material-icons">undo</span>
      </a>
      <a class="rounded-effect" class:disabled={pointer === history.length} on:click={redo}>
        <span title="redo" class="material-icons">redo</span>
      </a>
    </div>
    <Toolbar
      onChange={onToolbarChange}
      selectedData={activeData || null}
      selectedComponent={activeSection}
      bind:rawData={editorData}
      bind:this={toolbar}
      options={options}
      meta={meta} />
    <Editor bind:data={editorData} meta={meta} onFocus={onFocus} onBlur={onBlur} isFixed={isFixed} options={options} onChange={onEditorChange} />
  </div>
{:else}
  <style>
    .preview-area {
      overflow: auto;
    }

    .preview-area .wrapper {
      margin: 0 auto;
    }
  </style>
  <div class="full-size flex flex-row email-html-editor" style="height: 100vh;">
    <div class="col10 f-left preview-area t-center">
      <div class="wrapper full-size">
        <div class="f-left full-size" id="rawEmailTarget" />
      </div>
    </div>
    <div class="col10 f-left" style="background: #333; color: #ffff;">
      <textarea class="full-size" bind:this={textarea} bind:value={editorData.content} />
    </div>
  </div>
{/if}
